import { createDate } from "js/crossword/helpers/dateHelpers";
import { capitalizePhrase, capitalizeWord } from "../utils/misc-utils";
import moment from "moment";

type LocalizedContent = Record<string, string>;
type RelacementMap = Record<string, string | number>;

export interface ILocalizationProvider {
  getDate(date: string): ILocalizedDate;
  getMonthName(month: number): string;
  get: (
    key: string,
    replacementMap?: RelacementMap,
  ) => string;
}

export interface ILocalizedDate {
  day: number;
  year: number;
  ordinalDay: string;
  weekdayName: string;
  monthName: string;
  complete: string;
}

enum DateFormat {
  DAY_ORDINAL = "Do",
  WEEKDAY_NAME_FULL = "dddd",
  WEEKDAY_NAME_SHORT = "ddd",
  MONTH_NAME_FULL = "MMMM",
  COMPLETE_DATE_DEFAULT = "LL",
  COMPLETE_DATE_US= "MMMM Do, YYYY",
}

const completeDateFormat = {
  en: DateFormat.COMPLETE_DATE_US,
};

export default async function getLocalizationProvider(
  locale: string
): Promise<ILocalizationProvider> {
  moment.locale(locale);
  const localizedContent = await getLangData(locale);
  window.moment = moment;

  return {
    getMonthName,
    getDate: (date) => getLocalizedDate(date, completeDateFormat[locale]),
    get: (key, replacementMap) => getLocalizedText(localizedContent, key, replacementMap),
  };
}

function getMonthName(month: number): string {
  const date = new Date();
  date.setMonth(month);
  return capitalizeWord(
    moment(date).format(DateFormat.MONTH_NAME_FULL),
  );
}

function getLocalizedDate(
  dateStr: string,
  completeFormat?: DateFormat,
): ILocalizedDate {
  const date = createDate(dateStr);
  const mDate = moment(date);

  return {
    day: date.getDate(),
    year: date.getFullYear(),
    ordinalDay: mDate.format(DateFormat.DAY_ORDINAL),
    weekdayName: capitalizeWord(mDate.format(DateFormat.WEEKDAY_NAME_FULL)),
    monthName: capitalizeWord(mDate.format(DateFormat.MONTH_NAME_FULL)),
    complete: capitalizePhrase(mDate.format(completeFormat || DateFormat.COMPLETE_DATE_DEFAULT)),
  };
}

function getLocalizedText(
  localizedContent: LocalizedContent,
  key: string,
  replacementMap?: RelacementMap,
): string {
  const localized = localizedContent[key];

  if (typeof localized !== "string") {
    console.warn("Localized content not available for provided key:", key);
    return key;
  }

  if (replacementMap) return replaceInString(localized, replacementMap);

  return localized;
}

function replaceInString(text: string, replacementMap: RelacementMap) {
  let result = text;

  Object.keys(replacementMap).forEach(key => {
    result = result.split(`{${key}}`).join(`${replacementMap[key]}`);
  });

  return result;
}

function getLangData(locale: string): Promise<Record<string, string>> {
  return new Promise((resolve) => {
    const origin = document.location.origin + document.location.pathname;

    const req = new XMLHttpRequest();
    req.open("GET", `${origin}/lang/${locale}.json`, true);
    req.addEventListener("load", () => {
      try {
        const data = JSON.parse(req.responseText);
        resolve(data);
      } catch (error) {
        resolve({});
      }
    });
    req.addEventListener("error", () => resolve({}));
    req.send();
  });
}
